import { flexRender } from '@tanstack/react-table';
import type { Row, Cell } from '@tanstack/react-table';

import { TableBodyProps, TableRowData } from './types';

import Checkbox from '~/core/atoms/Checkbox';
import cn from '~/utils/cn';

const TableBody = <T extends TableRowData>({ table, selection, onRowClick, className }: TableBodyProps<T>) => {
  const handleRowSelect = (row: Row<T>, checked: boolean) => {
    row.toggleSelected(checked);
  };

  const handleRowClick = (row: Row<T>) => {
    if (onRowClick) {
      onRowClick(row.original);
    }
  };

  return (
    <tbody className={cn('divide-y divide-blue-100', className)}>
      {table.getRowModel().rows.map((row: Row<T>) => (
        <tr
          key={row.id}
          className={cn('hover:bg-bluesky-100/50 transition-colors', {
            'bg-bluesky-100/50': row.getIsSelected(),
            'cursor-pointer': !!onRowClick,
          })}
          onClick={() => handleRowClick(row)}
        >
          {/* Selection column */}
          {selection?.enabled && (
            <td
              className="p-8 first:pl-12 last:pr-12"
              onClick={(e) => e.stopPropagation()} // Prevent row click when clicking checkbox
            >
              <Checkbox
                size="sm"
                checked={row.getIsSelected()}
                onChange={(checked) => handleRowSelect(row, checked)}
                ariaLabel={`Select row ${row.index + 1}`}
              />
            </td>
          )}

          {/* Data columns */}
          {row.getVisibleCells().map((cell: Cell<T, unknown>) => {
            // Access custom properties from the original column definition
            const customColumnDef = cell.column.columnDef;
            const width = customColumnDef.size || 'auto';

            return (
              <td
                key={cell.id}
                className={cn('text-grey-900 px-4 py-12 text-base first:pl-12 last:pr-12')}
                style={{ width }}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            );
          })}
        </tr>
      ))}
    </tbody>
  );
};

export default TableBody;
